import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, Shield, Mic, MessageCircle, LogOut, User as UserIcon, Settings, Sparkles, BookOpen, Brain, Film, CheckSquare, Moon, Crown, Lock, Zap, Star, Edit, AlertTriangle, ExternalLink, GraduationCap, Calendar, MapPin, Activity } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import PrivacyDashboard from '@/components/PrivacyDashboard';
import { OracleCardsFan } from '@/components/OracleCardsFan';
import DailyCheckIn from '@/components/DailyCheckIn';
import CycleAndLunarHub from '@/components/CycleAndLunarHub';
import FutureSelfSpotlight from '@/components/FutureSelfSpotlight';
import NervousSystemReset from '@/components/NervousSystemReset';
import QuantumJournal from '@/components/QuantumJournal';
import { useSubscription, PRODUCTS, PRICES } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import bookLogo from '@/assets/book-logo.png';
import whoopLogo from '@/assets/whoop-logo.svg';
import therappiWhoopCombined from '@/assets/therappi-whoop-combined.svg';
import { logoConfig } from '@/config/logoSizes';
import ContentManager from '@/components/ContentManager';
import { useContentText } from '@/contexts/ContentContext';
import { useEditableContent } from '@/hooks/useEditableContent';
import { MobileNav } from '@/components/MobileNav';
import { DesktopNav } from '@/components/DesktopNav';
import { useUserRole } from '@/hooks/useUserRole';
import { LogoEditor, LogoEditorControls } from '@/components/LogoEditor';
import { useLogoEditor } from '@/contexts/LogoEditorContext';
import { cn } from '@/lib/utils';
import { AppHeader } from '@/components/AppHeader';
import { UnifiedWellnessScore } from '@/components/dashboard/UnifiedWellnessScore';
import { MorningBriefing } from '@/components/dashboard/MorningBriefing';
import { AchievementsDisplay } from '@/components/dashboard/AchievementsDisplay';
import MyWellnessChallengesCard from '@/components/challenges/MyWellnessChallengesCard';
import { RelationalInsightsCard } from '@/components/dashboard/RelationalInsightsCard';
import { TherapistConnectionCard } from '@/components/dashboard/TherapistConnectionCard';
const Dashboard = () => {
  const { trackPageView, trackFeatureUsage } = useAnalytics();
  const [user, setUser] = useState<User | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [showPrivacyDashboard, setShowPrivacyDashboard] = useState(false);
  const [showDailyCheckIn, setShowDailyCheckIn] = useState(false);
  const [showMenstrualTracker, setShowMenstrualTracker] = useState(false);
  const [showFutureSelfSpotlight, setShowFutureSelfSpotlight] = useState(false);
  const [showNervousSystemReset, setShowNervousSystemReset] = useState(false);
  const [showQuantumJournal, setShowQuantumJournal] = useState(false);
  const [showContentManager, setShowContentManager] = useState(false);
  const [showAdminContentManager, setShowAdminContentManager] = useState(false);
  const { isEditing: isEditingLogos, setIsEditing: setIsEditingLogos } = useLogoEditor();
  const {
    hasFeatureAccess,
    createPayment,
    createCheckout
  } = useSubscription();
  
  // Get user role - must be called before any conditional returns
  const { isAdmin } = useUserRole(user);

  // Get content from the content management system - ALL text is now editable!
  const dashboardTitle = useEditableContent('dashboard.title', 'Welcome to Your Wellness Journey', 'dashboard', 'Main dashboard welcome title');
  const dashboardSubtitle = useEditableContent('dashboard.subtitle', 'Your complete mind-body wellness companion. Access AI Therappi<sup className="text-[0.6em]">®</sup> in multiple evidence-based modes (CBT, DBT, ACT, Psychodynamic, Person-Centered, Somatic), integrate biometric insights, and explore holistic practices for profound transformation.', 'dashboard', 'Main dashboard subtitle');
  const appTitle = useEditableContent('header.app_title', 'Therappi®', 'header', 'Main app title in header');
  const appSubtitle = useEditableContent('header.app_subtitle', 'Where Science Meets Soul', 'header', 'App tagline in header');
  const privacyButtonText = useEditableContent('header.privacy_button', 'Privacy', 'ui', 'Privacy button text');
  const signoutButtonText = useEditableContent('header.signout_button', 'Sign Out', 'ui', 'Sign out button text');
  const editContentButtonText = useEditableContent('header.edit_content_button', 'Edit Content', 'ui', 'Edit content button text');
  const safeConfidentialText = useEditableContent('header.safe_confidential', 'Safe & Confidential', 'ui', 'Safe and confidential badge text');
  const dailyGuidanceTitle = useEditableContent('dashboard.daily_guidance_title', 'Daily Guidance', 'dashboard', 'Daily guidance section title');
  const dailyGuidanceDescription = useEditableContent('dashboard.daily_guidance_description', 'Swipe through the multi-dimensional oracle deck and let your intuition guide you to today\'s message.', 'dashboard', 'Daily guidance description');
  
  // Crisis & Safety Suite
  const crisisSuiteTitle = useEditableContent('features.crisis_suite.title', 'Crisis & Safety Suite', 'features', 'Crisis suite feature title');
  const crisisSuiteDescription = useEditableContent('features.crisis_suite.description', 'Immediate access to crisis resources, safety planning, and support tools when you need them most.', 'features', 'Crisis suite description');
  const crisisSuiteButton = useEditableContent('features.crisis_suite.button', 'Access Crisis Resources', 'features', 'Crisis suite button text');
  
  // WHOOP Integration
  const whoopIntegrationTitle = useEditableContent('features.whoop.title', 'Therappi<sup className="text-[0.6em]">®</sup> x WHOOP<sup className="text-[0.6em]">™</sup>', 'features', 'WHOOP integration feature title');
  const whoopIntegrationSubtitle = useEditableContent('features.whoop.subtitle', 'Mind, Body & Data in Harmony', 'features', 'WHOOP integration subtitle');
  const whoopIntegrationButton = useEditableContent('features.whoop.button', 'View Your Biometrics', 'features', 'WHOOP integration button text');
  
  // AI Therappi (Standalone Quick Chat)
  const aiTherappiTitle = useEditableContent('features.ai_therappi.title', 'AI Therappi<sup className="text-[0.6em]">®</sup>', 'features', 'AI Therappi feature title');
  const aiTherappiDescription = useEditableContent('features.ai_therappi.description', 'Quick access to your AI Therappi<sup className="text-[0.6em]">®</sup> companion. Start a supportive conversation instantly with intelligent guidance tailored to your needs.', 'features', 'AI Therappi description');
  const aiTherappiButton = useEditableContent('features.ai_therappi.button', 'Start Chat', 'features', 'AI Therappi button text');
  
  // The Sanctuary (Full Psychological Suite)
  const sanctuaryTitle = useEditableContent('features.sanctuary.title', 'The Sanctuary', 'features', 'Sanctuary feature title');
  const sanctuaryDescription = useEditableContent('features.sanctuary.description', 'Your complete psychological wellness suite. Access AI Therappi<sup className="text-[0.6em]">®</sup> modes (General Support, CBT, Psychotherapy, Timeline Integration), condition-specific tools, evidence-based skills library, and crisis support.', 'features', 'Sanctuary description');
  const sanctuaryButton = useEditableContent('features.sanctuary.button', 'Enter Sanctuary', 'features', 'Sanctuary button text');
  
  // Wellness Courses
  const wellnessCoursesTitle = useEditableContent('features.wellness_courses.title', 'Wellness Courses', 'features', 'Wellness courses feature title');
  const wellnessCoursesDescription = useEditableContent('features.wellness_courses.description', 'Access numerous guided programmes on multi-dimensional healing, personal growth and spiritual and intuitive development.', 'features', 'Wellness courses description');
  const wellnessCoursesButton = useEditableContent('features.wellness_courses.button', 'Explore Courses', 'features', 'Wellness courses button text');
  const proBadgeText = useEditableContent('ui.pro_badge', 'Pro', 'ui', 'Pro badge text');
  
  // Guided Meditations
  const guidedMeditationsTitle = useEditableContent('features.guided_meditations.title', 'Guided Meditations', 'features', 'Guided meditations feature title');
  const guidedMeditationsDescription = useEditableContent('features.guided_meditations.description', 'Find peace and re-programme your life with our collection of meditation and hypnosis sessions.', 'features', 'Guided meditations description');
  const guidedMeditationsButton = useEditableContent('features.guided_meditations.button', 'Start Meditating', 'features', 'Guided meditations button text');
  
  // Manifestation Magnifier
  const manifestationTitle = useEditableContent('features.manifestation.title', 'Manifestation Magnifier', 'features', 'Manifestation feature title');
  const manifestationDescription = useEditableContent('features.manifestation.description', 'Create and replay your own personalised manifestation movie curated from your images, videos, and personal affirmations.', 'features', 'Manifestation description');
  const manifestationButton = useEditableContent('features.manifestation.button', 'Create Movie', 'features', 'Manifestation button text');
  
  // Daily Check-In
  const dailyCheckInTitle = useEditableContent('features.daily_checkin.title', 'Daily Check-In', 'features', 'Daily check-in feature title');
  const dailyCheckInDescription = useEditableContent('features.daily_checkin.description', 'Connect with your inner wisdom and track elements of your wellness journey with our comprehensive daily self-assessment.', 'features', 'Daily check-in description');
  const dailyCheckInButton = useEditableContent('features.daily_checkin.button', 'Start Check-In', 'features', 'Daily check-in button text');
  
  // Cycle & Lunar Tracker (inclusive for menstruation, menopause & pregnancy)
  const menstrualTrackerTitle = useEditableContent('features.menstrual_tracker.title', 'Cycle & Lunar Tracker', 'features', 'Cycle & lunar tracker feature title');
  const menstrualTrackerDescription = useEditableContent('features.menstrual_tracker.description', 'Track your menstrual cycle, pregnancy journey, or menopause transition in harmony with lunar phases for deeper insights into your natural rhythms and balance.', 'features', 'Cycle tracker description');
  const menstrualTrackerButtonOpen = useEditableContent('features.menstrual_tracker.button_open', 'Open Tracker', 'features', 'Menstrual tracker open button');
  const menstrualTrackerButtonAccess = useEditableContent('features.menstrual_tracker.button_access', 'Get Access', 'features', 'Menstrual tracker get access button');
  const premiumBadgeText = useEditableContent('ui.premium_badge', 'Premium', 'ui', 'Premium badge text');
  
  // Nervous System Reset
  const nervousSystemTitle = useEditableContent('features.nervous_system.title', 'Nervous System Reset', 'features', 'Nervous system feature title');
  const nervousSystemDescription = useEditableContent('features.nervous_system.description', 'Regulate your nervous system with tools such as breathwork, EFT tapping, and somatic practices for instant calm.', 'features', 'Nervous system description');
  const nervousSystemButton = useEditableContent('features.nervous_system.button', 'Open Reset Tools', 'features', 'Nervous system button text');
  
  // Future Self Spotlight (merged with Navigator)
  const futureSelfTitle = useEditableContent('features.future_self.title', 'Future Self Spotlight', 'features', 'Future self feature title');
  const futureSelfDescription = useEditableContent('features.future_self.description', 'Your complete timeline transformation system: Map your destination, navigate your route with pivotal steps, embody your future self frequency through daily alignment practice, and turn the engine on with intentional action.', 'features', 'Future self description');
  const futureSelfButton = useEditableContent('features.future_self.button', 'Open Navigation System', 'features', 'Future self button text');
  
  // Quantum Journal
  const quantumJournalTitle = useEditableContent('features.quantum_journal.title', 'Quantum Journal', 'features', 'Quantum journal feature title');
  const quantumJournalDescription = useEditableContent('features.quantum_journal.description', 'A free-flow space to write, release and realign. Process your thoughts, emotions, and capture insights morning and evening.', 'features', 'Quantum journal description');
  const quantumJournalButton = useEditableContent('features.quantum_journal.button', 'Open Journal', 'features', 'Quantum journal button text');
  
  // Disclaimer text
  const disclaimerText = useEditableContent('ui.disclaimer', 'Therappi<sup className="text-[0.6em]">®</sup> is designed to support your wellness journey. It is not a replacement for professional therapy or medical advice. If you\'re experiencing a mental health crisis, please contact a qualified professional or emergency services.', 'ui', 'App disclaimer text');
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .single();
      
      if (data?.display_name) {
        setDisplayName(data.display_name.split(' ')[0]); // Get first name only
      }
    };
    
    fetchProfile();
  }, [user]);
  const handleMenstrualTrackerAccess = async () => {
    trackFeatureUsage('menstrual_tracker', 'attempted_access');
    
    if (hasFeatureAccess(PRODUCTS.MENSTRUAL_TRACKER)) {
      trackFeatureUsage('menstrual_tracker', 'opened');
      // Open the new journey-based hub
      setShowMenstrualTracker(true);
    } else {
      // Show purchase options
      const choice = window.confirm("Cycle & Lunar Tracker requires premium access.\n\n" + "✓ Premium Subscription ($29.99/month) - includes all features\n" + "✓ Standalone Purchase ($49.97 one-time) - lifetime access to tracker only\n\n" + "Click OK for Premium Subscription or Cancel for Standalone Purchase");
      try {
        const priceId = choice ? PRICES.PREMIUM : PRICES.MENSTRUAL_TRACKER;
        const url = choice ? await createCheckout(priceId) : await createPayment(priceId);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error creating checkout:', error);
        toast.error('Failed to create checkout session. Please try again.');
      }
    }
  };

  const handleSignOut = async () => {
    // Log sign out for compliance
    if (user) {
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'user_logout',
        p_details: {
          timestamp: new Date().toISOString()
        }
      });
    }
    await supabase.auth.signOut();
  };
  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>;
  }

  return <div className="min-h-screen bg-gradient-therapeutic">
      <AppHeader user={user} onSignOut={handleSignOut} />

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-foreground mb-3 sm:mb-4 px-2">
              {displayName ? `Welcome ${displayName}` : dashboardTitle}
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
              {dashboardSubtitle}
            </p>
          </div>

          {/* Morning Briefing */}
          <div className="mb-6 sm:mb-8">
            <MorningBriefing />
          </div>

          {/* My Wellness Challenges */}
          <div className="mb-6 sm:mb-8">
            <MyWellnessChallengesCard />
          </div>

          {/* Wellness Dashboard Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <UnifiedWellnessScore />
            <AchievementsDisplay />
          </div>

          {/* WHOOP Integration - Button Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="sm:col-span-8 sm:col-start-3">
              <Card className={cn(
                "p-0 bg-gradient-therapeutic shadow-therapy border-0 overflow-hidden hover:shadow-gentle transition-all duration-300"
              )}>
                {isEditingLogos ? (
                  <div className="flex items-center justify-center px-4 py-12">
                    <LogoEditor logoId="dashboard-therappi-whoop-combined" isEditing={true}>
                      <img src={therappiWhoopCombined} alt="Therappi x WHOOP" className="h-auto object-contain" style={{ width: '450px' }} />
                    </LogoEditor>
                  </div>
                ) : (
                  <Link to="/whoop-dashboard" className="block p-8 group relative">
                    <img src={therappiWhoopCombined} alt="Therappi x WHOOP" className="h-auto object-contain mx-auto" style={{ width: '145%' }} />
                    <div className="absolute inset-0 flex items-center justify-center bg-transparent group-hover:bg-muted/10 transition-colors">
                      <p className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                        View your WHOOP data
                      </p>
                    </div>
                  </Link>
                )}
              </Card>
            </div>
          </div>

          {/* Primary Therapy Features Grid - Full Width */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* AI Therappi - Quick Chat */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-primary/10 to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
                  <MessageCircle className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-light text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(aiTherappiTitle) }} />
              </div>
              <p className="text-muted-foreground mb-4 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(aiTherappiDescription) }} />
              <Link to="/therapy">
                <Button className="w-full" size="lg">
                  {aiTherappiButton}
                </Button>
              </Link>
            </Card>

            {/* The Sanctuary - Full Suite */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-secondary/10 to-secondary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
                  <Shield className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-light text-foreground">{sanctuaryTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sanctuaryDescription) }} />
              <Link to="/sanctuary">
                <Button className="w-full" size="lg" variant="outline">
                  {sanctuaryButton}
                </Button>
              </Link>
            </Card>
          </div>

          {/* Secondary Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">

            {/* Courses (Premium) */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 relative bg-gradient-to-br from-background to-primary/5">
              <div className="absolute top-3 right-3">
                <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">{proBadgeText}</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{wellnessCoursesTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{wellnessCoursesDescription}</p>
              <Link to="/course-library">
                <Button className="w-full" variant="outline">
                  {wellnessCoursesButton}
                </Button>
              </Link>
            </Card>

            {/* Daily Check-In */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <CheckSquare className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{dailyCheckInTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{dailyCheckInDescription}</p>
              <Link to="/daily-checkin">
                <Button className="w-full" variant="outline">
                  {dailyCheckInButton}
                </Button>
              </Link>
            </Card>

            {/* Meditations (Premium) */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 relative bg-gradient-to-br from-background to-primary/5">
              <div className="absolute top-3 right-3">
                <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">{proBadgeText}</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Brain className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{guidedMeditationsTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{guidedMeditationsDescription}</p>
              <Link to="/meditation-library">
                <Button className="w-full" variant="outline">
                  {guidedMeditationsButton}
                </Button>
              </Link>
            </Card>
          </div>

          {/* Health Insights & Analytics */}
          <div className="mb-6">
            <h3 className="text-xl font-light text-foreground mb-4">Health Insights & Analytics</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Correlation Explorer */}
              <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-accent/10 to-accent/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                    <Activity className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-light text-foreground">Correlation Explorer</h3>
                </div>
                <p className="text-muted-foreground mb-4 text-sm">
                  Discover powerful insights by analysing how your lifestyle choices impact your physical and mental wellbeing. See the hidden connections between diet, sleep, stress, WHOOP metrics, and more.
                </p>
                <Link to="/vault?tab=correlations">
                  <Button className="w-full" variant="outline">
                    <Activity className="h-4 w-4 mr-2" />
                    Explore Correlations
                  </Button>
                </Link>
              </Card>

              {/* My Scores Dashboard */}
              <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-accent/10 to-accent/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                    <Activity className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-light text-foreground">My Health Scores</h3>
                </div>
                <p className="text-muted-foreground mb-4 text-sm">
                  Track your Inflammation Load (IL) Score and Mental Wellness Score in one unified dashboard. Monitor trends and see how your overall health evolves over time.
                </p>
                <Link to="/my-scores">
                  <Button className="w-full" variant="outline">
                    <Activity className="h-4 w-4 mr-2" />
                    View My Scores
                  </Button>
                </Link>
              </Card>

              {/* Relational Insights */}
              <RelationalInsightsCard />
            </div>
          </div>

          {/* Free Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Film className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{manifestationTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{manifestationDescription}</p>
              <Link to="/manifestation">
                <Button className="w-full">
                  {manifestationButton}
                </Button>
              </Link>
            </Card>

            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{futureSelfTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{futureSelfDescription}</p>
              <Link to="/future-self-navigator">
                <Button className="w-full">
                  {futureSelfButton}
                </Button>
              </Link>
            </Card>

            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Moon className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-light text-foreground">{menstrualTrackerTitle}</h3>
                  {!hasFeatureAccess(PRODUCTS.MENSTRUAL_TRACKER) && <div className="flex items-center gap-1">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">{premiumBadgeText}</span>
                    </div>}
                </div>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{menstrualTrackerDescription}</p>
              <Button className="w-full" onClick={handleMenstrualTrackerAccess} variant={hasFeatureAccess(PRODUCTS.MENSTRUAL_TRACKER) ? "default" : "outline"}>
                {hasFeatureAccess(PRODUCTS.MENSTRUAL_TRACKER) ? menstrualTrackerButtonOpen : menstrualTrackerButtonAccess}
              </Button>
            </Card>

            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">Telempathy Tools</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">Develop your psychic, intuitive, and mediumship abilities with comprehensive tracking tools.</p>
              <Link to="/telempathy-tools">
                <Button className="w-full">
                  Open Tools
                </Button>
              </Link>
            </Card>

            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{quantumJournalTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">{quantumJournalDescription}</p>
              <Button className="w-full" onClick={() => setShowQuantumJournal(true)}>
                {quantumJournalButton}
              </Button>
            </Card>

            <Card className="p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Moon className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">Sleep & Dreams</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">Track sleep, understand dreams, improve rest quality with science-based tools and AI dream analysis.</p>
              <Link to="/sleep-and-dreams">
                <Button className="w-full">
                  Open Sleep Suite
                </Button>
              </Link>
            </Card>

          </div>

          {/* Four Tab Section: Therapist, Nervous System, Connect & Grow, The Vault */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-6 sm:mt-8">
            {/* My Therapist Card */}
            <TherapistConnectionCard userId={user?.id || ''} />
            {/* Nervous System Reset Tab */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 flex flex-col bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Zap className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">{nervousSystemTitle}</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm flex-1">{nervousSystemDescription}</p>
              <Button className="w-full" onClick={() => setShowNervousSystemReset(true)}>
                {nervousSystemButton}
              </Button>
            </Card>

            {/* Connect & Grow Tab */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 flex flex-col bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 1.5 L25 21 L3 21 Z" stroke="hsl(183, 29%, 16%)" strokeWidth="1.2" fill="none" strokeLinejoin="miter" />
                    <circle cx="14" cy="14" r="9" stroke="hsl(183, 29%, 16%)" strokeWidth="1.2" fill="none" />
                  </svg>
                </div>
                <h3 className="text-lg font-light text-foreground">Connect & Grow</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm flex-1">Join the community, access learning resources, and connect with The Psychic Psychologist<sup className="text-[0.6em]">®</sup></p>
              <Link to="/connect-and-grow">
                <Button className="w-full">
                  Explore Resources
                </Button>
              </Link>
            </Card>

            {/* The Vault Tab */}
            <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 flex flex-col bg-gradient-to-br from-background to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Lock className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-light text-foreground">The Vault</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm flex-1">Your complete personal archive and multi-dimensional insights hub. Access all your journals, recordings, manifestations, and track correlations across mind, body, and energy patterns.</p>
              <Link to="/vault">
                <Button className="w-full">
                  Open Vault
                </Button>
              </Link>
            </Card>
          </div>

          {/* Crisis & Safety Suite - Bottom Button */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 mt-6 sm:mt-8 mb-6 sm:mb-8">
            <div className="sm:col-span-8 sm:col-start-3">
              <Card className="p-4 bg-gradient-therapeutic shadow-therapy border-0 hover:shadow-gentle transition-all duration-300 hover:scale-105 h-20 sm:h-24">
                <Link to="/crisis" className="flex items-center justify-center h-full">
                  <Button className="w-full h-full bg-transparent hover:bg-white/10 text-primary flex items-center justify-center gap-2 sm:gap-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-6 w-6 sm:h-10 sm:w-10 text-primary" />
                    </div>
                    <div className="text-center">
                      <div className="text-sm sm:text-lg font-light text-primary">{crisisSuiteTitle}: {crisisSuiteButton}</div>
                    </div>
                  </Button>
                </Link>
              </Card>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 sm:mt-8 text-center px-4">
            <p className="text-xs text-muted-foreground max-w-2xl mx-auto leading-relaxed">{disclaimerText}</p>
          </div>
        </div>
      </main>
      
      {/* Privacy Dashboard Modal */}
      <PrivacyDashboard user={user} isOpen={showPrivacyDashboard} onClose={() => setShowPrivacyDashboard(false)} />

      {/* Logo Editor Controls - Desktop only */}
      <div className="hidden md:block">
        <LogoEditorControls isEditing={isEditingLogos} onToggle={() => setIsEditingLogos(!isEditingLogos)} />
      </div>

      {/* Daily Check-In Modal */}
      {showDailyCheckIn && <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border shadow-xl">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-light">Daily Wellness Check-In</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowDailyCheckIn(false)}>
                ✕
              </Button>
            </div>
            <div className="p-6">
              <DailyCheckIn onComplete={() => setShowDailyCheckIn(false)} />
            </div>
          </div>
        </div>}

      {/* Cycle & Lunar Tracker Modal */}
      {showMenstrualTracker && <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border shadow-xl">
            <div className="p-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Moon className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-light">Cycle & Lunar Tracker</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMenstrualTracker(false)}>
                ✕
              </Button>
            </div>
            <div className="p-6">
              <CycleAndLunarHub />
            </div>
          </div>
        </div>}

      {/* Nervous System Reset Modal */}
      <NervousSystemReset isOpen={showNervousSystemReset} onClose={() => setShowNervousSystemReset(false)} />

      {/* Future Self Spotlight Modal */}
      <FutureSelfSpotlight isOpen={showFutureSelfSpotlight} onClose={() => setShowFutureSelfSpotlight(false)} />

      {/* Quantum Journal Modal */}
      <QuantumJournal isOpen={showQuantumJournal} onClose={() => setShowQuantumJournal(false)} />
      
      {/* Content Manager Modal */}
      {showContentManager && <ContentManager onClose={() => setShowContentManager(false)} />}
    </div>;
};
export default Dashboard;